import axios from 'axios';

const ANTHROPIC_API_KEY = import.meta.env?.VITE_ANTHROPIC_API_KEY;
const ANTHROPIC_API_URL = 'https://api.anthropic.com/v1/messages';

/**
 * Maps Anthropic API error status codes to user-friendly error messages.
 * @param {number} statusCode - The HTTP status code from Anthropic API.
 * @param {Object} errorData - The error data from API response.
 * @returns {Object} Object with isInternal flag and error message.
 */
function getErrorMessage(statusCode, errorData) {
  if (statusCode === 401) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your Anthropic API key.' };
  } else if (statusCode === 403) {
    return { isInternal: true, message: 'Permission denied. Your API key does not have access to the specified resource.' };
  } else if (statusCode === 404) {
    return { isInternal: true, message: 'Resource not found. The requested endpoint or model may not exist.' };
  } else if (statusCode === 429) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (statusCode === 500) {
    return { isInternal: true, message: 'Anthropic service error. An unexpected error occurred on their servers. Please try again later.' };
  } else if (statusCode === 529) {
    return { isInternal: true, message: 'Anthropic service is temporarily overloaded. Please try again in a few moments.' };
  } else {
    return { isInternal: false, message: errorData?.error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Auto-reviews a contract for compliance, missing clauses, and legal risks using Anthropic AI.
 * @param {string} contractText - The full contract text to review.
 * @param {string} contractType - Type of contract (e.g., 'rental', 'sale', 'commercial').
 * @param {string} jurisdiction - Legal jurisdiction (e.g., 'UAE', 'Jordan', 'Saudi Arabia').
 * @returns {Promise<Object>} Analysis results with compliance, missing clauses, and risks.
 */
export async function autoReviewContract(contractText, contractType = 'rental', jurisdiction = 'UAE') {
  if (!ANTHROPIC_API_KEY) {
    throw new Error('Anthropic API key is not configured. Please add VITE_ANTHROPIC_API_KEY to your .env file.');
  }

  if (!contractText || contractText?.trim()?.length === 0) {
    throw new Error('Contract text is required for review.');
  }

  const prompt = `أنت خبير قانوني متخصص في عقود العقارات في ${jurisdiction}. قم بمراجعة العقد التالي بشكل شامل وتقديم تحليل مفصل.

نوع العقد: ${contractType}
النطاق القانوني: ${jurisdiction}

نص العقد:
${contractText}

يرجى تقديم تحليل شامل يتضمن:

1. **تقييم الامتثال القانوني** (Compliance Assessment):
   - مدى التزام العقد بالقوانين واللوائح المحلية
   - أي انتهاكات محتملة للقوانين العقارية
   - درجة الامتثال (ممتاز، جيد، مقبول، ضعيف)

2. **البنود المفقودة** (Missing Clauses):
   - قائمة بالبنود القانونية الأساسية المفقودة
   - أهمية كل بند مفقود (حرج، مهم، موصى به)
   - توصيات لإضافة هذه البنود

3. **المخاطر القانونية** (Legal Risks):
   - المخاطر المحتملة للأطراف المتعاقدة
   - مستوى كل خطر (عالي، متوسط، منخفض)
   - استراتيجيات التخفيف من المخاطر

4. **التوصيات** (Recommendations):
   - تعديلات مقترحة لتحسين العقد
   - بنود يجب إعادة صياغتها
   - نصائح عامة لتقوية العقد

5. **الخلاصة** (Summary):
   - تقييم عام للعقد
   - هل يُنصح بالتوقيع على العقد بحالته الحالية؟
   - الخطوات التالية الموصى بها

يرجى تقديم الإجابة بتنسيق JSON واضح ومنظم باللغة العربية.`;

  try {
    const response = await axios?.post(
      ANTHROPIC_API_URL,
      {
        model: 'claude-sonnet-4-5-20250929',
        max_tokens: 4096,
        temperature: 0.3,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ]
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ANTHROPIC_API_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true'
        }
      }
    );

    const content = response?.data?.content?.[0]?.text;
    if (!content) {
      throw new Error('No response content received from Anthropic API.');
    }

    // Try to parse JSON from the response
    let analysisResult;
    try {
      // Extract JSON if wrapped in markdown code blocks
      const jsonMatch = content?.match(/```json\s*([\s\S]*?)\s*```/) || content?.match(/```\s*([\s\S]*?)\s*```/);
      const jsonText = jsonMatch ? jsonMatch?.[1] : content;
      analysisResult = JSON.parse(jsonText);
    } catch (parseError) {
      // If JSON parsing fails, structure the text response
      analysisResult = {
        compliance: {
          assessment: 'تم تحليل العقد بنجاح',
          details: content,
          grade: 'جيد'
        },
        missingClauses: [],
        legalRisks: [],
        recommendations: [],
        summary: content,
        rawResponse: content
      };
    }

    return {
      success: true,
      analysis: analysisResult,
      reviewedAt: new Date()?.toISOString(),
      model: 'claude-sonnet-4-5',
      tokensUsed: response?.data?.usage?.input_tokens + response?.data?.usage?.output_tokens
    };
  } catch (error) {
    if (error?.response) {
      const errorInfo = getErrorMessage(error?.response?.status, error?.response?.data);
      const apiError = new Error(errorInfo.message);
      apiError.statusCode = error?.response?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Anthropic API error:', apiError);
      }
      throw apiError;
    }
    
    console.error('Contract review error:', error);
    throw new Error(error?.message || 'Failed to review contract. Please try again.');
  }
}

/**
 * Performs a quick compliance check on a contract.
 * @param {string} contractText - The contract text to check.
 * @param {string} contractType - Type of contract.
 * @returns {Promise<Object>} Quick compliance assessment.
 */
export async function quickComplianceCheck(contractText, contractType = 'rental') {
  if (!ANTHROPIC_API_KEY) {
    throw new Error('Anthropic API key is not configured.');
  }

  const prompt = `قم بإجراء فحص سريع للامتثال القانوني للعقد التالي:

نوع العقد: ${contractType}

نص العقد:
${contractText}

قدم تقييماً سريعاً يتضمن:
1. درجة الامتثال (ممتاز، جيد، مقبول، ضعيف)
2. أهم 3 مشاكل (إن وجدت)
3. توصية سريعة (موافق للتوقيع / يحتاج مراجعة / غير موصى به)

الرد يجب أن يكون مختصراً وواضحاً بتنسيق JSON.`;

  try {
    const response = await axios?.post(
      ANTHROPIC_API_URL,
      {
        model: 'claude-haiku-4-5-20251001',
        max_tokens: 1024,
        temperature: 0.2,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ]
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ANTHROPIC_API_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true'
        }
      }
    );

    const content = response?.data?.content?.[0]?.text;
    
    let result;
    try {
      const jsonMatch = content?.match(/```json\s*([\s\S]*?)\s*```/) || content?.match(/```\s*([\s\S]*?)\s*```/);
      const jsonText = jsonMatch ? jsonMatch?.[1] : content;
      result = JSON.parse(jsonText);
    } catch (parseError) {
      result = {
        grade: 'جيد',
        issues: [],
        recommendation: content,
        rawResponse: content
      };
    }

    return {
      success: true,
      quickCheck: result,
      checkedAt: new Date()?.toISOString()
    };
  } catch (error) {
    if (error?.response) {
      const errorInfo = getErrorMessage(error?.response?.status, error?.response?.data);
      const apiError = new Error(errorInfo.message);
      apiError.statusCode = error?.response?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Anthropic API error:', apiError);
      }
      throw apiError;
    }
    
    console.error('Quick compliance check error:', error);
    throw new Error(error?.message || 'Failed to perform quick compliance check.');
  }
}

/**
 * Identifies specific legal risks in a contract.
 * @param {string} contractText - The contract text to analyze.
 * @param {string} partyRole - Role of the party (e.g., 'landlord', 'tenant', 'buyer', 'seller').
 * @returns {Promise<Object>} Identified legal risks.
 */
export async function identifyLegalRisks(contractText, partyRole = 'tenant') {
  if (!ANTHROPIC_API_KEY) {
    throw new Error('Anthropic API key is not configured.');
  }

  const prompt = `أنت مستشار قانوني. قم بتحليل العقد التالي من منظور ${partyRole} وحدد المخاطر القانونية المحتملة:

نص العقد:
${contractText}

حدد:
1. المخاطر القانونية الرئيسية
2. مستوى كل خطر (عالي، متوسط، منخفض)
3. التأثير المحتمل على ${partyRole}
4. استراتيجيات التخفيف من المخاطر

قدم الإجابة بتنسيق JSON واضح باللغة العربية.`;

  try {
    const response = await axios?.post(
      ANTHROPIC_API_URL,
      {
        model: 'claude-sonnet-4-5-20250929',
        max_tokens: 2048,
        temperature: 0.3,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ]
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ANTHROPIC_API_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true'
        }
      }
    );

    const content = response?.data?.content?.[0]?.text;
    
    let risks;
    try {
      const jsonMatch = content?.match(/```json\s*([\s\S]*?)\s*```/) || content?.match(/```\s*([\s\S]*?)\s*```/);
      const jsonText = jsonMatch ? jsonMatch?.[1] : content;
      risks = JSON.parse(jsonText);
    } catch (parseError) {
      risks = {
        risks: [],
        summary: content,
        rawResponse: content
      };
    }

    return {
      success: true,
      legalRisks: risks,
      analyzedAt: new Date()?.toISOString()
    };
  } catch (error) {
    if (error?.response) {
      const errorInfo = getErrorMessage(error?.response?.status, error?.response?.data);
      const apiError = new Error(errorInfo.message);
      apiError.statusCode = error?.response?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Anthropic API error:', apiError);
      }
      throw apiError;
    }
    
    console.error('Legal risk identification error:', error);
    throw new Error(error?.message || 'Failed to identify legal risks.');
  }
}
